import './PricingMatrix.css';
import React, { useState } from 'react';
import { EventProductVariant, PricePart, PricingCategory, getEventDetails } from '../../../../../model/event';
import { ModalController } from '../../../../../components/Modal/ControlledModal';
import { TextField } from '../../../../../components/Form/TextField/TextField';
import { Button } from '../../../../../components/Button/Button';
import { cloneDeep, compact } from 'lodash';
import { Icon } from '../../../../../components/Icon/Icon';
import { Datepicker } from '../../../../../components/Form/Datepicker/Datepicker';
import { Switcher } from '../../../../../components/Form/Switcher/Switcher';
import classnames from 'classnames';
import { Form } from '../../../../../components/Form/Form';
import { round, removeTax, sequence } from '../../../../../util/util';
import { NumberField } from '../../../../../components/Form/NumberField/NumberField';
import { guid } from 'redux-database/dist/util';
import { api, writeDB } from '../../../../../model/store';
import { handleError } from '../../../../../model/notification';
import { t } from '../../../../../locales/translate';
import { SimpleCheckbox } from '../../../../../components/Form/Checkbox/Checkbox';
import { Stack } from '../../../../../components/Stack/Stack';
import { applyTax } from '../../../../../model/price';

interface Props {
  controller: ModalController<PricingCategory>;
  simplified?: boolean;
  germanyTaxRate: number;
}

export function pricingCategoryTotal(productVariant: EventProductVariant): number {
  return round(
    productVariant.basePrice +
      productVariant.presaleFee +
      productVariant.systemFee +
      productVariant.priceParts.reduce((sum, part) => sum + part.price, 0),
    6
  );
}

export function PricingMatrix({ controller, simplified, germanyTaxRate }: Props): JSX.Element | null {
  const [connected] = useState(false);
  const [basePriceMode] = useState(true);
  const [vatMode, setVatMode] = useState(true);
  const [addedProductVariantIds, setAddedProductVariantIds] = useState<string[]>([]);
  const [productVariantChanges, setProductVariantChanges] = useState<{
    [id: string]: Partial<EventProductVariant>;
  }>({});
  const allProductVariants: EventProductVariant[] = [
    ...controller.model.productVariants,
    ...addedProductVariantIds.map((e) => ({ id: e })),
  ].map(
    (e) =>
      ({
        ...e,
        ...(productVariantChanges[e.id] || {}),
      }) as EventProductVariant
  );

  const productVariants = allProductVariants.filter((e) => !e._delete);
  const productVariantGroups = sequence(
    productVariants,
    (a, b) => a.physical !== b.physical && a.description === b.description,
    2
  );

  const labels = simplified
    ? ['name', 'vat', 'body-header', 'baseprice']
    : [
        'name',
        'vat',
        'active-from',
        'active-until',
        'private',
        'resale-allowed',
        'body-header',
        'vat-mode',
        'baseprice',
        'pre-sale-fee',
        'system-fee',
      ];
  const footerLabels = simplified
    ? ['add-pricepart', 'total', 'remove']
    : ['add-pricepart', 'total', 'remove', 'refund-deduction', 'seller-fee', 'buyer-fee'];

  if (productVariants.length < 1) {
    return null;
  }

  const presaleFeePercentage = productVariants[0].presaleFeePercentage;
  const allVariantsIds = productVariants.map((e) => e.id);

  function calculateGrossSellerFee(productVariant: EventProductVariant): number {
    if (productVariant.sellerFee !== null) {
      // net value is returned from server
      return applyTax(productVariant.sellerFee, germanyTaxRate);
    }

    // calculated gross value
    return (productVariant.presaleFee + productVariant.systemFee) * (1 + productVariant.taxRate);
  }

  function variantsForIds(ids: string[]): EventProductVariant[] {
    return compact(ids.map((id) => productVariants.find((e) => e.id === id)));
  }

  function update(ids: string[], props: Partial<EventProductVariant>): void {
    setProductVariantChanges((s) => {
      const state = cloneDeep(s);
      ids.forEach((id) => {
        state[id] = { ...(state[id] || {}), ...props };
      });
      return state;
    });
  }

  function onPricePartChange<T extends keyof PricePart>(
    variantIds: string[],
    pricePartIndex: number,
    field: T,
    value: PricePart[T]
  ): void {
    const variants = variantsForIds(variantIds);
    const baseVariant = variants[0] || productVariants[0];

    const newPriceparts = cloneDeep(baseVariant.priceParts);
    if (field === 'description') {
      newPriceparts[pricePartIndex][field] = value;
    } else if (field === 'price') {
      const taxRate = variants[0].taxRate;
      newPriceparts[pricePartIndex].price = round(vatMode ? removeTax(value as number, taxRate) : value, 6);
    }

    update(variantIds, {
      priceParts: newPriceparts,
    });
  }

  function onPresaleFeePercentageChange(value: number): void {
    productVariants.forEach((variant) => {
      update([variant.id], {
        presaleFeePercentage: value,
        presaleFee: round(variant.basePrice * value, 6),
      });
    });
  }

  function onBasePriceChange({ basePriceChange, ids }: { basePriceChange: number; ids: string[] }): void {
    const taxRate = variantsForIds(ids)[0].taxRate;
    const basePrice = round(vatMode ? removeTax(basePriceChange, taxRate) : basePriceChange, 6);
    const changes: Partial<EventProductVariant> = {
      basePrice,
    };
    if (presaleFeePercentage) {
      changes.presaleFee = round(basePrice * presaleFeePercentage, 6);
    }
    update(ids, changes);
  }

  function onTotalChange({ totalChange, id }: { totalChange: number; id: string }): void {
    const productVariant = variantsForIds([id])[0];
    const taxRate = productVariant.taxRate;
    const total = vatMode ? removeTax(totalChange, taxRate) : totalChange;
    const sumOfPriceParts = productVariant.priceParts.reduce((sum, e) => sum + e.price, 0);

    const changes: Partial<EventProductVariant> = {};
    if (presaleFeePercentage) {
      changes.basePrice = round((total - sumOfPriceParts - productVariant.systemFee) / (1 + presaleFeePercentage), 6);
      changes.presaleFee = round(changes.basePrice * (1 + presaleFeePercentage), 6);
    } else {
      changes.basePrice = round(total - sumOfPriceParts - productVariant.presaleFee - productVariant.systemFee, 6);
    }

    update([id], changes);
  }

  function onAddPricePart(): void {
    productVariants.forEach((e) =>
      update([e.id], {
        priceParts: [...e.priceParts, { description: '', price: 0 }],
      })
    );
  }

  async function handleOnSubmit(): Promise<void> {
    const category = controller.model;
    // added variants only have a temporary client-side id, the backend assigns the real one
    const variants = allProductVariants
      .filter((e) => !!productVariantChanges[e.id])
      .map(({ id, ...variant }) => (addedProductVariantIds.includes(id) ? variant : { id, ...variant }));

    try {
      if (productVariants.some((item) => !item.description)) {
        throw new Error(t.errors.requiredVariantsDescription);
      }

      const { data } = await api.updatePricingCategory({
        eventId: category.eventId,
        pricingCategoryId: category.id,
        productVariants: variants,
      });

      writeDB.table('pricingCategories').update(data.id, data as PricingCategory);
      // the OrderID needed to display the Billing Item tab only comes from the Event Endpoint
      await getEventDetails({ eventId: controller.model.eventId });
      controller.close();
    } catch (error) {
      handleError(error);
    }
  }

  function addVariant(): void {
    const newVariants = productVariantGroups[0]
      .map(cloneDeep)
      .map((e) => ({ ...e, id: guid(), description: '', resaleAllowed: !e.local }));
    newVariants.forEach((variant) => update([variant.id], variant));
    const newIds = newVariants.map((e) => e.id);
    setAddedProductVariantIds((state) => [...state, ...newIds]);
  }

  function removeVariants(ids: string[]): void {
    if (addedProductVariantIds.some((id) => ids.includes(id))) {
      setAddedProductVariantIds((addedIds) => addedIds.filter((id) => !ids.includes(id)));
    }
    update(ids, { _delete: true });
  }

  // function handleReconnect() {
  //   productVariants.map((variant, index) => {
  //     if (index % 2 !== 0) {
  //       update([variant.id], {
  //         basePrice: productVariants[index - 1].basePrice,
  //         presaleFee: productVariants[index - 1].presaleFee,
  //         presaleFeePercentage: productVariants[index - 1].presaleFeePercentage,
  //         systemFee: productVariants[index - 1].systemFee,
  //         priceParts: cloneDeep(productVariants[index - 1].priceParts),
  //         activeFrom: productVariants[index - 1].activeFrom,
  //       });
  //     }
  //   });
  // }

  function updateVat(ids: string[], value: number): void {
    update(ids, {
      taxRate: value,
    });
  }

  function renderLabel(label: string): JSX.Element | null {
    switch (label) {
      case 'name':
        return (
          <div className="pricing-matrix__label">
            <span>{t.model.variant._name}</span>
            <Icon name="question--sm" />
          </div>
        );
      case 'vat':
        return <div className="pricing-matrix__label">{t.model.price.vat}</div>;
      case 'active-from':
        return (
          <>
            <div className="pricing-matrix__label">
              <span>{t.model.productVariant.activeFrom}</span>
              <Icon name="question--sm" />
            </div>
            {/* <div
              className="pricing-matrix__connect"
              onClick={() =>
                setConnected(value => {
                  if (!value) {
                    handleReconnect();
                  }
                  return !value;
                })
              }
            >
              <Icon name={connected ? 'disconnect--sm' : 'chain--sm'} />
              <span>{connected ? 'Edit Individually' : 'Edit Together'}</span>
            </div> */}
          </>
        );
      case 'active-until':
        return (
          <div className="pricing-matrix__label">
            <span>{t.model.productVariant.activeUntil}</span>
            <Icon name="question--sm" />
          </div>
        );
      case 'private':
        return (
          <div className="pricing-matrix__label">
            <span>{t.model.productVariant.private}</span>
          </div>
        );
      case 'resale-allowed':
        return (
          <div className="pricing-matrix__label">
            <span>{t.model.productVariant.resaleAllowed}</span>
          </div>
        );
      case 'body-header':
        return <div className="pricing-matrix__body-header">{t.model.price.calculatePrice}</div>;
      case 'vat-mode':
        return (
          <div className="pricing-matrix__label">
            <Icon name="plus--sm" className="icon-placeholder" />
            <span>{t.model.price.calculationMode}</span>
          </div>
        );
      case 'baseprice':
        return (
          <div className="pricing-matrix__label">
            {basePriceMode ? (
              <Icon
                style={{ color: 'red' }}
                name="calculator"
                // onClick={() => setBasePriceMode(false)}
              />
            ) : (
              <Icon
                name="calculator"
                // onClick={() => setBasePriceMode(true)}
              />
            )}
            <span>{t.model.price.basePrice}</span>
          </div>
        );
      case 'pre-sale-fee':
        return (
          <div className="pricing-matrix__label">
            <Icon name="plus--sm" className="icon-placeholder" />
            <NumberField
              className="pricing-matrix__presale-fee-label"
              value={presaleFeePercentage}
              percentage
              onChange={(val) => {
                onPresaleFeePercentageChange(val);
              }}
            />
            {t.model.price.presaleFee}
          </div>
        );
      case 'system-fee':
        return (
          <div className="pricing-matrix__label">
            <Icon name="plus--sm" className="icon-placeholder" />
            <span>{t.model.price.systemFee}</span>
          </div>
        );
      case 'total':
        return (
          <div className="pricing-matrix__label">
            {!basePriceMode ? (
              <Icon
                style={{ color: 'red' }}
                name="calculator"
                // onClick={() => setBasePriceMode(true)}
              />
            ) : (
              <Icon
                name="calculator"
                // onClick={() => setBasePriceMode(false)}
              />
            )}
            <span>{t.general.total}</span>
          </div>
        );
      case 'refund-deduction':
        return (
          <div className="pricing-matrix__label">
            <span>{t.model.price.refundDeduction}</span>
          </div>
        );
      case 'seller-fee':
        return (
          <div className="pricing-matrix__label">
            <Stack size="xxs">
              <span>{t.model.price.resaleSellerFee}</span>
              <SimpleCheckbox
                label={t.model.price.defaultValue}
                value={productVariants[0].sellerFee === null ? true : false}
                onChange={() => {
                  productVariants.forEach((productVariant) => {
                    update([productVariant.id], {
                      sellerFee:
                        productVariant.sellerFee === null
                          ? removeTax(calculateGrossSellerFee(productVariant), germanyTaxRate)
                          : null,
                    });
                  });
                }}
              />
            </Stack>
          </div>
        );
      case 'buyer-fee':
        return (
          <div className="pricing-matrix__label">
            <Stack size="xxs">
              <span>{t.model.price.resaleBuyerFee}</span>
              <SimpleCheckbox
                label={t.model.price.defaultValue}
                value={productVariants[0].buyerFee === null ? true : false}
                onChange={() => {
                  productVariants.forEach((productVariant) => {
                    update([productVariant.id], {
                      buyerFee: productVariant.buyerFee === null ? 0 : null,
                    });
                  });
                }}
              />
            </Stack>
          </div>
        );
      default:
        return null;
    }
  }

  function renderContent(
    label: string,
    productVariant: EventProductVariant,
    connectedProductVariant: EventProductVariant | null,
    groupSize: number,
    index: number
  ): JSX.Element | null {
    if (connected && index % 2 !== 0) {
      return null;
    }

    const idAndConnectedId = compact([productVariant.id, connectedProductVariant?.id]);
    const idsToUpdate = connected ? idAndConnectedId : [productVariant.id];

    switch (label) {
      case 'name':
        if (index % 2 !== 0) {
          return null;
        }
        return (
          <td colSpan={groupSize} key={`name"-${index}`}>
            <TextField
              value={productVariant.description}
              onChange={(value) => {
                update(idAndConnectedId, {
                  description: value as string,
                });
              }}
            />
          </td>
        );
      case 'vat':
        if (index % 2 !== 0) {
          return null;
        }
        return (
          <td colSpan={groupSize} key={`vat"-${index}`}>
            <NumberField
              percentage
              decimalDigits={4}
              value={productVariant.taxRate}
              onChange={(value) => updateVat(idAndConnectedId, value)}
            />
          </td>
        );
      case 'active-from':
        if (index % 2 !== 0) {
          return null;
        }
        return (
          <td className="pricing-matrix__active-from" colSpan={groupSize} key={`active-from-${index}`}>
            <Form
              model={{
                online: productVariant.activeFrom,
                hard: connectedProductVariant?.activeFrom,
              }}
              onInput={(m, key) => {
                if (key === 'online') {
                  update([productVariant.id], {
                    activeFrom: m.online,
                  });
                }
                if (key === 'hard' && connectedProductVariant) {
                  update([connectedProductVariant.id], {
                    activeFrom: m.hard,
                  });
                }
              }}
            >
              <Datepicker icon={false} name="online" hourpicker />
              {!connected && <Datepicker icon={false} name="hard" hourpicker />}
            </Form>
          </td>
        );
      case 'active-until':
        if (index % 2 !== 0) {
          return null;
        }
        return (
          <td className="pricing-matrix__active-from" colSpan={groupSize} key={`active-until-${index}`}>
            <Form
              model={{
                online: productVariant.activeUntil,
                hard: connectedProductVariant?.activeUntil,
              }}
              onInput={(m, key) => {
                if (key === 'online') {
                  update([productVariant.id], {
                    activeUntil: m.online,
                  });
                }
                if (key === 'hard' && connectedProductVariant) {
                  update([connectedProductVariant.id], {
                    activeUntil: m.hard,
                  });
                }
              }}
            >
              <Datepicker icon={false} name="online" hourpicker />
              {!connected && <Datepicker icon={false} name="hard" hourpicker />}
            </Form>
          </td>
        );
      case 'private':
        return (
          <td className="pricing-matrix__hidden" key={`hidden"-${index}`}>
            <SimpleCheckbox
              value={productVariant.private}
              onChange={() =>
                update(idsToUpdate, {
                  private: !productVariant.private,
                })
              }
            />
          </td>
        );
      case 'resale-allowed':
        return (
          <td className="pricing-matrix__hidden" key={`hidden"-${index}`}>
            <SimpleCheckbox
              value={productVariant.resaleAllowed}
              onChange={() =>
                update(idsToUpdate, {
                  resaleAllowed: !productVariant.resaleAllowed,
                })
              }
            />
          </td>
        );
      case 'body-header':
        if (simplified) return <td key={`body-header" />-${index}`} />;
        if (index % 2 === 0) {
          return (
            <td key={`body-header" />-${index}`}>
              <div className="pricing-matrix__body-header color-text-label">{t.model.ticket.hardticket}</div>
            </td>
          );
        }
        return (
          <td key={`body-header" />-${index}`}>
            <div className="pricing-matrix__body-header color-text-label">{t.model.ticket.onlineticket}</div>
          </td>
        );
      case 'vat-mode':
        return (
          <td className="pricing-matrix__vat-mode" key={`vat-mode"-${index}`}>
            <Switcher
              value={!vatMode}
              onChange={(val) => setVatMode(!val)}
              labels={[t.model.price.net, t.model.price.gross]}
              disabled={index % 2 !== 0 ? true : false}
            />
          </td>
        );
      case 'baseprice':
        return (
          <td key={`baseprice"-${index}`}>
            <div className="pricing-matrix__calc-cell">
              <NumberField
                value={productVariant.basePrice ? productVariant.basePrice : 0}
                onChange={(val) => {
                  onBasePriceChange({
                    ids: idsToUpdate,
                    basePriceChange: val,
                  });
                }}
                decimalDigits={6}
                disabled={!basePriceMode || vatMode}
                displayErrorOnBlur
              />
              <NumberField
                value={applyTax(productVariant.basePrice, productVariant.taxRate)}
                onChange={(val) => {
                  onBasePriceChange({
                    ids: idsToUpdate,
                    basePriceChange: val,
                  });
                }}
                disabled={!basePriceMode || !vatMode}
                displayErrorOnBlur
              />
            </div>
          </td>
        );
      case 'pre-sale-fee':
        return (
          <td key={`presale-fee"-${index}`}>
            <div className="pricing-matrix__calc-cell">
              <NumberField
                value={productVariant.presaleFee}
                onChange={(val) =>
                  update(idsToUpdate, {
                    presaleFee: val,
                    presaleFeePercentage: undefined,
                  })
                }
                decimalDigits={6}
                disabled={vatMode}
                displayErrorOnBlur
              />
              <NumberField
                value={applyTax(productVariant.presaleFee, productVariant.taxRate)}
                onChange={(val) => {
                  update(idsToUpdate, {
                    presaleFee: removeTax(val, productVariant.taxRate),
                    presaleFeePercentage: undefined,
                  });
                }}
                disabled={!vatMode}
                displayErrorOnBlur
              />
            </div>
          </td>
        );
      case 'system-fee':
        return (
          <td key={`system fee"-${index}`}>
            <div className="pricing-matrix__calc-cell">
              <NumberField
                value={productVariant.systemFee}
                onChange={(val) =>
                  update(idsToUpdate, {
                    systemFee: round(val, 6),
                  })
                }
                decimalDigits={6}
                disabled={vatMode}
                displayErrorOnBlur
              />
              <NumberField
                value={applyTax(productVariant.systemFee, productVariant.taxRate)}
                onChange={(val) =>
                  update(idsToUpdate, {
                    systemFee: removeTax(val, productVariant.taxRate),
                  })
                }
                disabled={!vatMode}
                displayErrorOnBlur
              />
            </div>
          </td>
        );
      case 'add-pricepart':
        return (
          <td className="pricing-matrix__add-pricepart" key={`add-pricepart"-${index}`}>
            <div className="pricing-matrix__add-pricepart-button">
              <Button title={t.model.price.addNewPricepart} icon="plus-circle--sm" onClick={onAddPricePart} />
            </div>
          </td>
        );
      case 'total':
        return (
          <td className="pricing-matrix__total" key={`total"-${index}`}>
            <div className="pricing-matrix__calc-cell">
              <NumberField
                value={pricingCategoryTotal(productVariant)}
                disabled={basePriceMode || !vatMode}
                onChange={(val) => onTotalChange({ totalChange: val, id: productVariant.id })}
                decimalDigits={6}
                displayErrorOnBlur
              />
              <NumberField
                value={applyTax(pricingCategoryTotal(productVariant), productVariant.taxRate)}
                disabled={basePriceMode || vatMode}
                onChange={(val) => onTotalChange({ totalChange: val, id: productVariant.id })}
                displayErrorOnBlur
              />
            </div>
          </td>
        );
      case 'refund-deduction':
        return (
          <td key={`refund-deduction"-${index}`}>
            <div className="pricing-matrix__calc-cell">
              <NumberField
                value={productVariant.refundDeduction}
                onChange={(val) =>
                  update(idsToUpdate, {
                    refundDeduction: round(val, 6),
                  })
                }
                decimalDigits={6}
                disabled={vatMode}
                displayErrorOnBlur
              />
              <NumberField
                value={
                  productVariant.refundDeduction
                    ? applyTax(productVariant.refundDeduction, productVariant.taxRate)
                    : undefined
                }
                onChange={(val) =>
                  update(idsToUpdate, {
                    refundDeduction: removeTax(val, productVariant.taxRate),
                  })
                }
                disabled={!vatMode}
                displayErrorOnBlur
              />
            </div>
          </td>
        );
      case 'seller-fee':
        return (
          <td key={`seller-fee-${index}`}>
            <div className="pricing-matrix__calc-cell">
              <NumberField
                value={removeTax(calculateGrossSellerFee(productVariant), germanyTaxRate)}
                onChange={(val) =>
                  update(idsToUpdate, {
                    sellerFee: val,
                  })
                }
                decimalDigits={6}
                disabled={vatMode}
                displayErrorOnBlur
                id="sellerFee"
              />
              <NumberField
                value={calculateGrossSellerFee(productVariant)}
                onChange={(val) =>
                  update(idsToUpdate, {
                    sellerFee: removeTax(val, germanyTaxRate),
                  })
                }
                disabled={!vatMode}
                displayErrorOnBlur
                id="sellerFeeGross"
              />
            </div>
          </td>
        );
      case 'buyer-fee':
        return (
          <td key={`buyer-fee-${index}`}>
            <div className="pricing-matrix__calc-cell">
              <NumberField
                value={productVariant.buyerFee || 0}
                onChange={(val) =>
                  update(idsToUpdate, {
                    buyerFee: round(val, 6),
                  })
                }
                decimalDigits={6}
                disabled={vatMode}
                displayErrorOnBlur
                id="buyerFee"
              />
              <NumberField
                value={applyTax(productVariant.buyerFee || 0, germanyTaxRate)}
                onChange={(val) =>
                  update(idsToUpdate, {
                    buyerFee: removeTax(val, germanyTaxRate),
                  })
                }
                disabled={!vatMode}
                displayErrorOnBlur
                id="buyerFeeGross"
              />
            </div>
          </td>
        );
      case 'remove':
        if (index % 2 !== 0) {
          return null;
        }
        return (
          <td className="pricing-matrix__remove" colSpan={groupSize} key={`remove-${index}`}>
            {index > 1 && (
              <Button
                onClick={() => {
                  removeVariants(idAndConnectedId);
                }}
                title={t.actions.remove('')}
              />
            )}
          </td>
        );
      default:
        return null;
    }
  }

  controller.configure({
    canSubmit: Object.keys(productVariantChanges).length > 0,
    onSubmit: handleOnSubmit,
    actions: () => (
      <>
        <Button onClick={addVariant} icon="plus" title={t.actions.add(t.model.variant._name)} />
      </>
    ),
  });

  return (
    <div className="pricing-matrix__wrapper">
      <table
        className={classnames('pricing-matrix', {
          'pricing-matrix--connected': connected,
          'pricing-matrix--disconnected': !connected,
          'pricing-matrix--simplified': simplified,
        })}
      >
        <tbody>
          {labels.map((label) => {
            return (
              <tr key={label}>
                <td>{renderLabel(label)}</td>
                {productVariantGroups.map((group, index) => {
                  return (
                    <>
                      {renderContent(label, group[0], group[1], group.length, index * 2)}
                      {group[1] && renderContent(label, group[1], group[0], group.length, index * 2 + 1)}
                    </>
                  );
                })}
              </tr>
            );
          })}
          {productVariants[0] &&
            productVariants[0].priceParts.map((_, index) => {
              return (
                <tr key={index}>
                  <td>
                    <div className="pricing-matrix__price-part-label">
                      <Icon
                        name="remove-circle--sm"
                        onClick={() => {
                          productVariants.forEach((productVariant) => {
                            update([productVariant.id], {
                              priceParts: productVariant.priceParts.filter((_1, idx) => index !== idx),
                            });
                          });
                        }}
                      />
                      <TextField
                        value={productVariants[0].priceParts[index].description}
                        onChange={(value) => onPricePartChange(allVariantsIds, index, 'description', String(value))}
                      />
                    </div>
                  </td>
                  {productVariants.map((productVariant, variantIndex) => {
                    if (connected && variantIndex % 2 !== 0) {
                      return;
                    }
                    return (
                      <td key={productVariant.id}>
                        <div className="pricing-matrix__calc-cell">
                          <NumberField
                            value={productVariant.priceParts[index].price}
                            onChange={(value) => {
                              onPricePartChange([productVariant.id], index, 'price', value);
                            }}
                            decimalDigits={6}
                            disabled={vatMode}
                            displayErrorOnBlur
                          />
                          <NumberField
                            value={applyTax(productVariant.priceParts[index].price, productVariant.taxRate)}
                            onChange={(value) => {
                              onPricePartChange([productVariant.id], index, 'price', value);
                            }}
                            disabled={!vatMode}
                            displayErrorOnBlur
                          />
                        </div>
                      </td>
                    );
                  })}
                </tr>
              );
            })}
          {footerLabels.map((label) => {
            return (
              <tr key={label}>
                <td>{renderLabel(label)}</td>
                {productVariantGroups.map((group, index) => {
                  return (
                    <>
                      {renderContent(label, group[0], group[1], group.length, index * 2)}
                      {group[1] && renderContent(label, group[1], group[0], group.length, index * 2 + 1)}
                    </>
                  );
                })}
              </tr>
            );
          })}
        </tbody>
      </table>
    </div>
  );
}
